import { useState } from "react";
import moment, { Moment } from "moment";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// ui related
import {
  Button,
  Typography,
  Stack,
  IconButton,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  Checkbox,
  Popover,
} from "@mui/material";
import { CalGrid } from "./Components/CalGrid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { ViewOrder } from "../Components/ViewOrder";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { ordersByCustomerDateSelector, ordersSelector } from "Redux/selectors";
import { useSession } from "Context/userAuthContext";
import {
  getDateHumanMode,
  getDateMMDDYYYY,
  getNewDateMMDDYYYY,
} from "Util/dateUtil";
import _ from "lodash";
import { updateProfileAsync } from "Redux/Reducers/sellerProfile";
import { CalMobileDay } from "./Components/CalMobileDay";
import { toast } from "react-toastify";
import BlockIcon from "@mui/icons-material/Block";
import { useParams } from "react-router-dom";
import { Availability } from "Components/BusinessView/Availability";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import { CheckBox } from "@mui/icons-material";

// Shared Block Month Popover Component
interface BlockMonthPopoverProps {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  monthString: string; // Format: "YYYY-MM" or any date that moment can parse
  onClose: () => void;
  onConfirm: () => void;
}

const BlockMonthPopover: React.FC<BlockMonthPopoverProps> = ({
  open,
  anchorEl,
  monthString,
  onClose,
  onConfirm,
}) => {
  return (
    <Popover
      id="blockMonthPopover"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography sx={{ mb: 1, maxWidth: "400px" }}>
          This will make all dates in {moment(monthString).format("MMMM YYYY")}{" "}
          unavailable for new requests. Would you like to proceed?
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button variant="text" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={onConfirm}>
            Block month
          </Button>
        </Stack>
      </Box>
    </Popover>
  );
};

const Calendar = () => {
  let { orderId } = useParams<"orderId">();
  const allOrders = useAppSelector(ordersSelector);
  const getOrderFromId = (orderId: string | undefined) => {
    if (orderId === undefined) {
      return undefined;
    }
    return allOrders.find((i) => i.orderId === orderId);
  };
  const orderFromUrl = getOrderFromId(orderId);

  const { user, sellerProfile } = useSession();
  const dispatch = useAppDispatch();

  // Desktop states
  const [selectedMonthMoment, setSelectedMonthMoment] = useState(
    orderFromUrl === undefined
      ? moment()
      : moment(new Date(orderFromUrl.customerDate))
  );
  const [selectedMonthString, setSelectedMonthString] = useState(
    selectedMonthMoment.format("YYYY-MM")
  );

  // Shared states for block month popover
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  const openDeleteRequest = Boolean(anchorEl);

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Mobile-only states
  const [mobileSelectedDay, setMobileSelectedDay] = useState(
    orderFromUrl === undefined
      ? new Date()
      : new Date(orderFromUrl.customerDate)
  );
  const [mobileDisplayedMonth, setMobileDisplayedMonth] = useState(
    orderFromUrl === undefined
      ? new Date()
      : new Date(orderFromUrl.customerDate)
  );
  const [mobileDayBlocked, setMobileDayBlocked] = useState(
    sellerProfile.availability.dateBlocked.findIndex(
      (d) => d === getNewDateMMDDYYYY()
    ) !== -1
  );

  const ordersByCustomerDate = useAppSelector(ordersByCustomerDateSelector);

  // Mobile-only function
  const toggleBlocked = () => {
    const newValueString = getDateMMDDYYYY(mobileSelectedDay);
    setMobileDayBlocked(!mobileDayBlocked);
    let dateBlockedCopy = _.clone(sellerProfile.availability.dateBlocked);

    const currentDate = new Date();
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(currentDate.getDate() - 2);

    // only keep last 2 days
    dateBlockedCopy = dateBlockedCopy.filter((d) => {
      const blockedDate = new Date(d);
      return blockedDate >= twoDaysAgo;
    });

    if (mobileDayBlocked) {
      toast(
        `${moment(mobileSelectedDay).format("MMM DD, YYYY")} was unblocked from your availability.`,
        {
          position: "top-center",
          autoClose: 1600,
        }
      );
      dateBlockedCopy = dateBlockedCopy.filter((d) => d !== newValueString);
    } else {
      toast(
        `${moment(mobileSelectedDay).format("MMM DD, YYYY")} was blocked from your availability.`,
        {
          position: "top-center",
          autoClose: 1600,
        }
      );
      dateBlockedCopy.push(newValueString);
    }
    if (user) {
      dispatch(
        updateProfileAsync({
          uid: user?.uid,
          sellerProfile: {
            ...sellerProfile,
            availability: {
              ...sellerProfile.availability,
              dateBlocked: dateBlockedCopy,
            },
          },
        })
      );
    }
  };

  // Shared function for both mobile and desktop
  const handleBlockMonth = (monthDate: Date | string) => {
    let dateBlockedCopy = _.clone(sellerProfile.availability.dateBlocked);
    const currentMonth = moment(monthDate);
    const daysInMonth = currentMonth.daysInMonth();

    // Loop through each day of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = getDateMMDDYYYY(
        new Date(currentMonth.year(), currentMonth.month(), i)
      );
      if (!dateBlockedCopy.includes(date)) {
        dateBlockedCopy.push(date);
      }
    }

    const currentDate = new Date();
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(currentDate.getDate() - 2);

    // Only keep last 2 days
    dateBlockedCopy = dateBlockedCopy.filter((d) => {
      const blockedDate = new Date(d);
      return blockedDate >= twoDaysAgo;
    });

    if (user) {
      dispatch(
        updateProfileAsync({
          uid: user?.uid,
          sellerProfile: {
            ...sellerProfile,
            availability: {
              ...sellerProfile.availability,
              dateBlocked: dateBlockedCopy,
            },
          },
        })
      );
    }

    toast(
      `All dates in ${currentMonth.format(
        "MMMM YYYY"
      )} has been blocked from your availability.`,
      {
        position: "top-center",
        autoClose: 1600,
      }
    );
    handleClosePopover();
  };

  // MOBILE VERSION
  if (smallScreen) {
    return (
      <Stack spacing={2}>
        <BlockMonthPopover
          open={openDeleteRequest}
          anchorEl={anchorEl}
          monthString={moment(mobileDisplayedMonth).format("YYYY-MM")}
          onClose={handleClosePopover}
          onConfirm={() => handleBlockMonth(mobileDisplayedMonth)}
        />

        <Stack sx={{ backgroundColor: "white", borderRadius: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              views={["day"]}
              slots={{
                day: CalMobileDay,
              }}
              slotProps={{
                day: {} as any,
              }}
              value={mobileSelectedDay}
              onChange={(newValue) => {
                if (newValue) {
                  setMobileSelectedDay(newValue);
                  setMobileDisplayedMonth(newValue);
                  const newValueString = getDateMMDDYYYY(newValue);
                  setMobileDayBlocked(
                    newValue < new Date() ||
                      sellerProfile.availability.dateBlocked.findIndex(
                        (d) => d === newValueString
                      ) !== -1
                  );
                }
              }}
              onMonthChange={(newMonth) => {
                setMobileDisplayedMonth(newMonth);
              }}
              sx={{
                width: "100%",
                "& .MuiPickersCalendarHeader-root": { pl: 2, pr: 1 },
                "& .MuiDayCalendar-header": { justifyContent: "space-evenly" },
                "& .MuiDayCalendar-weekContainer": {
                  justifyContent: "space-evenly",
                  margin: "8px 0",
                },
              }}
            />
          </LocalizationProvider>

          <Divider />
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            divider={<Divider orientation="vertical" flexItem />}
            justifyContent="space-evenly"
            sx={{ px: 1.5 }}
          >
            <Button
              size="small"
              variant="text"
              color="secondary"
              disabled={moment().isSame(moment(mobileDisplayedMonth), "month")}
              onClick={() => {
                const today = new Date();
                setMobileSelectedDay(today);
                setMobileDisplayedMonth(today);
                const todayString = getDateMMDDYYYY(today);
                setMobileDayBlocked(
                  sellerProfile.availability.dateBlocked.findIndex(
                    (d) => d === todayString
                  ) !== -1
                );
              }}
              sx={{ py: 1.5 }}
            >
              Today
            </Button>
            <Availability>
              <Button
                color="secondary"
                variant="text"
                size="small"
                sx={{ display: smallScreen ? "block" : "none" }}
              >
                Update availability
              </Button>
            </Availability>
            <Button
              size="small"
              disabled={moment().isAfter(moment(mobileDisplayedMonth), "month")}
              color="secondary"
              sx={{ width: "fit-content" }}
              onClick={handleClickPopover}
            >
              Block month
            </Button>
          </Stack>
        </Stack>

        {moment(mobileSelectedDay).isSame(mobileDisplayedMonth, "month") && (
          <Stack spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">
                {moment(mobileSelectedDay).format("MMMM DD, YYYY")}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mobileDayBlocked}
                    onChange={toggleBlocked}
                  />
                }
                labelPlacement="start"
                label="Block day"
                disabled={mobileSelectedDay < new Date()}
                sx={{
                  opacity: mobileSelectedDay < new Date() ? 0 : 1,
                }}
              />
            </Stack>
            {ordersByCustomerDate.get(getDateMMDDYYYY(mobileSelectedDay))
              ?.length ? (
              ordersByCustomerDate
                .get(getDateMMDDYYYY(mobileSelectedDay))
                ?.map((order) => (
                  <ViewOrder
                    view="kanban"
                    orderInfo={order}
                    key={order.orderId}
                  />
                ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No orders
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    );
  }

  // DESKTOP VERSION
  return (
    <Stack>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" columnGap={1}>
          {/* display month header */}
          <Typography variant="h6" flex={1}>
            {moment(selectedMonthString).format("MMMM")}{" "}
            {moment(selectedMonthString).format("YYYY")}
          </Typography>
          {/* switch month */}
          <Stack direction="row" alignItems="center">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                setSelectedMonthMoment(
                  selectedMonthMoment.subtract(1, "months")
                );
                setSelectedMonthString(selectedMonthMoment.format("YYYY-MM"));
              }}
            >
              <ArrowBackIosNewRoundedIcon fontSize="inherit" />
            </IconButton>
            <Button
              size="small"
              sx={{ height: "fit-content" }}
              disabled={moment().isSame(selectedMonthMoment, "month")}
              onClick={() => {
                setSelectedMonthMoment(moment());
                setSelectedMonthString(moment().format("YYYY-MM"));
              }}
            >
              Today
            </Button>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                setSelectedMonthMoment(selectedMonthMoment.add(1, "months"));
                setSelectedMonthString(selectedMonthMoment.format("YYYY-MM"));
              }}
            >
              <ArrowForwardIosRoundedIcon fontSize="inherit" />
            </IconButton>
          </Stack>
          <Divider orientation="vertical" sx={{ height: "20px" }} />
          <Availability>
            <Button color="secondary" size="small">
              Update availability
            </Button>
          </Availability>
          <Divider orientation="vertical" sx={{ height: "20px" }} />
          <Button
            size="small"
            disabled={moment().isAfter(selectedMonthMoment, "month")}
            color="secondary"
            sx={{ height: "fit-content" }}
            onClick={handleClickPopover}
          >
            Block month
          </Button>
          <BlockMonthPopover
            open={openDeleteRequest}
            anchorEl={anchorEl}
            monthString={selectedMonthString}
            onClose={handleClosePopover}
            onConfirm={() => handleBlockMonth(selectedMonthString)}
          />
        </Stack>

        <Stack direction="row" sx={{ pb: 1 }}>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Sun
          </Typography>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Mon
          </Typography>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Tue
          </Typography>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Wed
          </Typography>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Thu
          </Typography>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Fri
          </Typography>
          <Typography flex={1} variant="subtitle2" sx={{ ml: 1 }}>
            Sat
          </Typography>
        </Stack>
      </Stack>

      <CalGrid month={selectedMonthString} />
    </Stack>
  );
};

export default Calendar;
